import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	DEFAULT_FROM_TIME_VALUE,
	MAX_DEFAULT_TILL_TIME_VALUE,
	MIN_DEFAULT_TILL_TIME_VALUE,
	VALIDATION_TEXTS_TIME_INPUTS,
	validateYear,
} from 'common/activeElements/modals/hooks/const';
import { useFormWithTimeInputs } from 'common/activeElements/modals/hooks/useFormWithTimeInput';
import { standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { crewMemberActionLogStore } from 'pages/CrewMemberDetails/components/CrewMemberActionLog/CrewMemberActionLogStore';
import { crewMemberDetailsStore } from 'pages/CrewMemberDetails/crewMemberDetailsStore';
import { CREATE_CREW_MEMBER_HOLD_DOWN } from 'pages/CrewMemberDetails/gql/createCrewMemberHoldDown';
import { UPDATE_CREW_MEMBER_HOLD_DOWN } from 'pages/CrewMemberDetails/gql/updateCrewMemberHoldDown';
import { resetTimestampToUtc, updateDateWithTimeAndTZ } from 'utils/datetime';
import { handleError } from 'utils/error-parser';

const DEFAULT_VALUES = {
	from: '',
	fromTime: '',
	till: '',
	tillTime: '',
	crewMemberId: '',
	originalCrewMemberName: '',
	originalCrewMemberId: '',
	note: '',
};

const getInitialFormValues = (initialData) => {
	if (!initialData) return DEFAULT_VALUES;

	return {
		...DEFAULT_VALUES,
		...initialData,
	};
};

const buildHoldDownPayload = ({ values, baseTimezone }) => {
	const { from, fromTime, till, tillTime, crewMemberId, originalCrewMemberId, note } = values;
	const newFrom = updateDateWithTimeAndTZ(from, fromTime, baseTimezone);
	const newTo = updateDateWithTimeAndTZ(till, tillTime, baseTimezone);

	return {
		crewMemberId,
		originalCrewMemberId,
		effectiveTimestamp: resetTimestampToUtc(newFrom),
		expireTimestamp: resetTimestampToUtc(newTo),
		note,
	};
};

export const useModalSetOnHoldDown = ({ initialData, employeeId, baseTimezone, onClose }) => {
	const [driverName, setDriverName] = useState('');

	const [createCrewMemberHoldDown, { loading: isCreating }] = useMutation(
		CREATE_CREW_MEMBER_HOLD_DOWN,
		{ fetchPolicy: 'network-only' }
	);
	const [updateCrewMemberHoldDown, { loading: isUpdating }] = useMutation(
		UPDATE_CREW_MEMBER_HOLD_DOWN,
		{ fetchPolicy: 'network-only' }
	);

	const initialFormValues = useMemo(
		() => getInitialFormValues({ crewMemberId: employeeId, ...initialData }),
		[employeeId, initialData]
	);

	const {
		miDay,
		isValid,
		control,
		defineMinDay,
		handleResetForm,
		setValue,
		getValues,
		trigger,
		watch,
		reset,
	} = useFormWithTimeInputs(initialFormValues);

	useEffect(() => {
		reset(initialFormValues);
		setDriverName(initialFormValues.originalCrewMemberName ?? '');
	}, [initialFormValues, reset]);

	const setOriginalCrewMemberId = useCallback(
		(id) => setValue('originalCrewMemberId', id, { shouldDirty: true }),
		[setValue]
	);

	const [from, fromTime, till, tillTime, originalCrewMemberId] = watch([
		'from',
		'fromTime',
		'till',
		'tillTime',
		'originalCrewMemberId',
	]);

	const minEndTime = useMemo(() => {
		if (from && fromTime && till && from === till) return fromTime;

		return MIN_DEFAULT_TILL_TIME_VALUE;
	}, [from, fromTime, till]);

	const holdDownId = initialData?.id;
	const isEditMode = Boolean(holdDownId);
	const isSubmitting = isCreating || isUpdating;
	const isStartTimeDisabled = !from;
	const isEndDisabled = !from || !fromTime;
	const isEndTimeDisabled = !from || !fromTime || !till;
	const isSubmitDisabled =
		isEndTimeDisabled || !tillTime || !originalCrewMemberId || !isValid || isSubmitting;

	const validateEndTime = useCallback(
		(value) => {
			const { from: start, fromTime: startTime, till: end } = getValues();

			if (!start || !startTime || !end || !value) return true;

			try {
				const startTimestamp = updateDateWithTimeAndTZ(start, startTime, baseTimezone);
				const endTimestamp = updateDateWithTimeAndTZ(end, value, baseTimezone);

				if (endTimestamp === startTimestamp) {
					return VALIDATION_TEXTS_TIME_INPUTS.endTimeEqualValidationMsg;
				}

				if (endTimestamp < startTimestamp) {
					return VALIDATION_TEXTS_TIME_INPUTS.endTimeGreaterValidationMsg;
				}
			} catch {
				return true;
			}

			return true;
		},
		[getValues, baseTimezone]
	);

	const isValidDate = useCallback(() => {
		const { from: start, till: end } = getValues();

		if (!start || !end) return true;
		if (start > end) return 'End date must not precede start date';
		return true;
	}, [getValues]);

	const handleStartDateChange = useCallback(
		(field, e) => {
			const newStartDate = e.target.value;
			const { till: currentTill } = getValues();

			field.onChange(newStartDate);
			setValue('fromTime', DEFAULT_FROM_TIME_VALUE, { shouldDirty: true, shouldValidate: true });

			if (currentTill && newStartDate > currentTill) {
				setValue('till', newStartDate, { shouldDirty: true, shouldValidate: true });
			}

			if (currentTill) trigger('till');

			defineMinDay();
		},
		[getValues, setValue, trigger, defineMinDay]
	);

	const handleStartTimeChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);
			trigger('tillTime');
		},
		[trigger]
	);

	const handleEndDateChange = useCallback(
		(field, e) => {
			const { tillTime: currentTillTime } = getValues();

			field.onChange(e.target.value);

			if (!currentTillTime) {
				setValue('tillTime', MAX_DEFAULT_TILL_TIME_VALUE, {
					shouldDirty: true,
					shouldValidate: true,
				});
			} else {
				trigger('tillTime');
			}

			trigger('from');
		},
		[getValues, setValue, trigger]
	);

	const handleEndTimeChange = useCallback((field, e) => {
		field.onChange(e.target.value);
	}, []);

	const resetModalState = useCallback(() => {
		handleResetForm();
		setDriverName('');
		setOriginalCrewMemberId('');
	}, [handleResetForm, setOriginalCrewMemberId]);

	const handleClose = useCallback(() => {
		resetModalState();
		onClose();
	}, [resetModalState, onClose]);

	const handleSubmit = useCallback(async () => {
		if (!employeeId) {
			handleError(new Error('Crew member ID is missing.'));
			return;
		}

		try {
			const payload = buildHoldDownPayload({ values: getValues(), baseTimezone });
			const variables = isEditMode ? { id: holdDownId, input: payload } : { input: payload };

			const mutation = isEditMode ? updateCrewMemberHoldDown : createCrewMemberHoldDown;

			const { data } = await mutation({ variables });

			const result = isEditMode ? data?.updateCrewMemberHoldDown : data?.createCrewMemberHoldDown;
			const { errors } = result ?? {};

			if (errors?.length) {
				handleError(errors);
				return;
			}

			notificationStore.setNotification(standardSuccess);
			resetModalState();
			crewMemberActionLogStore.fetchLog();

			await crewMemberDetailsStore.fetchAll();

			onClose();
		} catch (error) {
			handleError(error);
		}
	}, [
		employeeId,
		isEditMode,
		updateCrewMemberHoldDown,
		createCrewMemberHoldDown,
		getValues,
		baseTimezone,
		holdDownId,
		resetModalState,
		onClose,
	]);

	return {
		control,
		miDay,
		driverName,
		minEndTime,
		maxEndTime: MAX_DEFAULT_TILL_TIME_VALUE,
		isSubmitDisabled,
		isStartTimeDisabled,
		isEndDisabled,
		isEndTimeDisabled,
		setDriverName,
		setOriginalCrewMemberId,
		isValidDate,
		validateYear,
		validateEndTime,
		handleStartDateChange,
		handleStartTimeChange,
		handleEndDateChange,
		handleEndTimeChange,
		handleSubmit,
		handleClose,
	};
};
